// src/components/PageSection.js -

// Import section components
import ButtonSection from '../page-sections/ButtonSection';
import ColumnsSection from '../page-sections/ColumnsSection';
import ContainerSection from '../page-sections/ContainerSection';
import CtaSection from '../page-sections/CtaSection';
import GallerySection from '../page-sections/GallerySection';
import HeaderDividedSection from '../page-sections/HeaderDividedSection';
import HeroSection from '../page-sections/HeroSection';
import ImageSection from '../page-sections/ImageSection';
import PopupSection from '../page-sections/PopupSection';
import RichTextSection from '../page-sections/RichTextSection';
import SlideshowHeaderWaveSection from '../page-sections/SlideshowHeaderWaveSection';
import SlideshowLinearSection from '../page-sections/SlideshowLinearSection';
import SlideshowSection from '../page-sections/SlideshowSection';
import SpacerSection from '../page-sections/SpacerSection';
import TextSection from '../page-sections/TextSection';

import TeamMemberSection from '../page-sections/TeamMemberSection';

import ContactFormSection from '../page-sections/ContactFormSection';
import GoogleMapsSection from '../page-sections/GoogleMapsSection';

import ProductCatalogSection from '../page-sections/ProductCatalogSection';
import ProductCatalogVerticalSection from '../page-sections/ProductCatalogVerticalSection';

import BlogPostsSection from '../page-sections/BlogPostsSection';
import FeaturedProductsSection from '../page-sections/FeaturedProductsSection';
import FeaturesSection from '../page-sections/FeaturesSection';
import NewsletterSection from '../page-sections/NewsletterSection';
import StaticContentSection from '../page-sections/StaticContentSection';
import StatisticsSection from '../page-sections/StatisticsSection';
import TestimonialsSection from '../page-sections/TestimonialsSection';




const PageSection = ({ section }) => {
  if (!section) return null;

  const { type, content, settings, children } = section;

  // Check if section should be hidden based on viewport
  const isMobile = window.innerWidth < 768;
  if ((isMobile && settings?.mobileHidden) || (!isMobile && settings?.desktopHidden)) {
    return null;
  }

  // Determine container class
  let containerClass = settings?.fullWidth ? 'w-full' : 'container mx-auto px-4';
  if (settings?.containerClass) {
    containerClass += ` ${settings.containerClass}`;
  }

  // Create inline style object from settings
  const sectionStyle = {};
  // Add content styles
  if (content?.backgroundColor) sectionStyle.backgroundColor = content.backgroundColor;
  if (content?.customBackgroundColor) sectionStyle.backgroundColor = content.customBackgroundColor;
  if (content?.textColor) sectionStyle.color = content.textColor;
  if (content?.customTextColor) sectionStyle.color = content.customTextColor;

  // Dimension properties
  if (content?.width) sectionStyle.width = content.width;
  if (content?.maxWidth) sectionStyle.maxWidth = content.maxWidth;
  if (content?.height) sectionStyle.height = content.height;
  if (content?.minHeight) sectionStyle.minHeight = content.minHeight;
  if (content?.maxHeight) sectionStyle.maxHeight = content.maxHeight;

  // Padding
  if (content?.padding) sectionStyle.padding = content.padding;
  if (content?.paddingTop) sectionStyle.paddingTop = content.paddingTop;
  if (content?.paddingRight) sectionStyle.paddingRight = content.paddingRight;
  if (content?.paddingBottom) sectionStyle.paddingBottom = content.paddingBottom;
  if (content?.paddingLeft) sectionStyle.paddingLeft = content.paddingLeft;

  // Margin
  if (content?.margin) sectionStyle.margin = content.margin;
  if (content?.marginTop) sectionStyle.marginTop = content.marginTop;
  if (content?.marginRight) sectionStyle.marginRight = content.marginRight;
  if (content?.marginBottom) sectionStyle.marginBottom = content.marginBottom;
  if (content?.marginLeft) sectionStyle.marginLeft = content.marginLeft;

  // Position properties from settings
  if (settings?.position && settings.position !== 'static') {
    sectionStyle.position = settings.position;
    if (content?.top) sectionStyle.top = content.top;
    if (content?.right) sectionStyle.right = content.right;
    if (content?.bottom) sectionStyle.bottom = content.bottom;
    if (content?.left) sectionStyle.left = content.left;
    if (settings?.zIndex) sectionStyle.zIndex = settings.zIndex;
  }

  // Border properties
  if (content?.borderWidth && content?.borderStyle && content.borderStyle !== 'none') {
    sectionStyle.borderWidth = content.borderWidth;
    sectionStyle.borderStyle = content.borderStyle;
    sectionStyle.borderColor = content.borderColor || '#000';
  }
  if (content?.borderRadius) sectionStyle.borderRadius = content.borderRadius;

  // Typography properties
  if (content?.fontFamily) sectionStyle.fontFamily = content.fontFamily.replace('font-', '');
  if (content?.fontSize) sectionStyle.fontSize = content.fontSize;
  if (content?.fontWeight) sectionStyle.fontWeight = content.fontWeight.replace('font-', '');
  if (content?.lineHeight) sectionStyle.lineHeight = content.lineHeight.replace('leading-', '');
  if (content?.letterSpacing) sectionStyle.letterSpacing = content.letterSpacing.replace('tracking-', '');
  if (content?.textTransform) sectionStyle.textTransform = content.textTransform;

  // Background properties
  if (content?.backgroundImage) sectionStyle.backgroundImage = `url(${content.backgroundImage})`;
  if (content?.backgroundSize) sectionStyle.backgroundSize = content.backgroundSize;
  if (content?.backgroundPosition) sectionStyle.backgroundPosition = content.backgroundPosition;
  if (content?.backgroundRepeat) sectionStyle.backgroundRepeat = content.backgroundRepeat;
  if (content?.backgroundAttachment) sectionStyle.backgroundAttachment = content.backgroundAttachment;

  // If background gradient is enabled
  if (content?.backgroundGradient && content?.gradientFrom && content?.gradientTo) {
    sectionStyle.backgroundImage = `linear-gradient(${content.gradientDirection || 'to bottom'}, ${content.gradientFrom}, ${content.gradientTo})`;
  }

  // Overflow
  if (settings?.overflow) sectionStyle.overflow = settings.overflow;

  // Add custom CSS if provided
  if (settings?.customCss) {
    // Parse custom CSS into style object
    const cssRules = settings.customCss.split(';').filter(rule => rule.trim());
    cssRules.forEach(rule => {
      const [property, value] = rule.split(':').map(part => part.trim());
      if (property && value) {
        // Convert kebab-case to camelCase
        const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        sectionStyle[camelProperty] = value;
      }
    });
  }

  // Render the appropriate section component based on type
  const renderSection = () => {
    switch (type) {
      case 'hero':
        return <HeroSection content={content} />;
      case 'headerDivided':
        return <HeaderDividedSection content={content} />;
      case 'text':
        return <TextSection content={content} />;
      // case 'richText':
      //   return content.richText ? <div className="rich-text">{parse(content.richText)}</div> : null;
      case 'richText':
        return <RichTextSection content={content} />;
      case 'button':
        return <ButtonSection content={content} />;
      case 'image':
        return <ImageSection content={content} />;
      case 'gallery':
        return <GallerySection content={content} />;
      case 'cta':
        return <CtaSection content={content} />;
      case 'columns':
        return <ColumnsSection content={content} />;
      case 'spacer':
        return <SpacerSection content={content} />;
      case 'container':
        return <ContainerSection content={content} children={children || []} />;
      case 'slideshow':
        return <SlideshowSection content={content} />;
      case 'slideshowLinear':
        return <SlideshowLinearSection content={content} />;
      case 'teamMembers':
        return <TeamMemberSection content={content} />;
      case 'googleMaps':
        return <GoogleMapsSection content={content} />;
      case 'contactForm':
        return <ContactFormSection content={content} sectionId={section._id} />;
      case 'productCatalog':
        return <ProductCatalogSection content={content} />;
      case 'slideshowHeaderWave':
        return <SlideshowHeaderWaveSection content={content} />;
      case 'productCatalogVertical':
        return <ProductCatalogVerticalSection content={content} />;
      case 'staticContent':
        return <StaticContentSection content={content} />;
      case 'featuredProducts':
        return <FeaturedProductsSection content={content} />;
      case 'statistics':
        return <StatisticsSection content={content} />;
      case 'blogPosts':
        return <BlogPostsSection content={content} />;
      case 'features':
        return <FeaturesSection content={content} />;
      case 'newsletter':
        return <NewsletterSection content={content} />;
      case 'testimonials':
        return <TestimonialsSection content={content} />;
      case 'popup':
        return <PopupSection content={content} />;

      default:
        return <div>Unknown section type: {type}</div>;
    }
  };

  return (
    <section className={containerClass} style={sectionStyle}>
      {renderSection()}
    </section>
  );
};

export default PageSection;